import { useFlag } from '@reflag/react-sdk';

import * as api from '@gitbook/api-client';
import type { NavigationSiteInsightsReports } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import type { IconDefinition } from '@gitbook/spine-react';

import { useTrialingSiteFeaturePlan } from '../../state';

export type SiteNewAnalyticsReport = Extract<
    (typeof NavigationSiteInsightsReports)[number],
    'traffic' | 'engagement' | 'agents' | 'assistant' | 'ads'
>;

/** What decides which rebuilt reports are listed. */
export interface SiteNewAnalyticsTabContext {
    site: api.Site;
    /** Engagement takes searches, feedback and link clicks out of Traffic and the legacy reports. */
    isEngagementEnabled: boolean;
}

export interface SiteNewAnalyticsTab {
    id: SiteNewAnalyticsReport;
    label: string;
    icon: IconDefinition;
    /** A report only some sites have; listed everywhere when absent. */
    visible?: (context: SiteNewAnalyticsTabContext) => boolean;
}

/**
 * Reports kept in the rebuilt analytics screen, listed from the main sidebar.
 */
export const siteNewAnalyticsTabs: SiteNewAnalyticsTab[] = [
    {
        id: 'traffic',
        label: 'Traffic',
        icon: Icon.MaximumValue,
    },
    {
        id: 'agents',
        label: 'Agents & MCP',
        icon: Icon.AI,
    },
    {
        id: 'assistant',
        label: 'AI Assistant',
        icon: Icon.SearchOrAsk,
    },
    {
        id: 'engagement',
        label: 'Engagement',
        icon: Icon.Users,
        visible: ({ isEngagementEnabled }) => isEngagementEnabled,
    },
    {
        id: 'ads',
        label: 'Ads & sponsorship',
        icon: Icon.PaidMember,
        visible: ({ site }) => Boolean(site.ads),
    },
];

/**
 * The rebuilt reports a site has, i.e. the ones its sidebar, breadcrumb and screen agree on.
 */
export function getVisibleSiteNewAnalyticsTabs(
    context: SiteNewAnalyticsTabContext
): SiteNewAnalyticsTab[] {
    return siteNewAnalyticsTabs.filter((tab) => tab.visible?.(context) ?? true);
}

/** The flag behind Engagement, read the same way by every surface that lists the reports. */
export function useSiteNewAnalyticsTabContext(site: api.Site): SiteNewAnalyticsTabContext & {
    isLoading: boolean;
} {
    const { isEnabled, isLoading } = useFlag('ANALYTICS_ENGAGEMENT_REPORT');
    return { site, isEngagementEnabled: isEnabled, isLoading };
}

/** Where a legacy report's data lives on the rebuilt screen, for links that predate it. */
const LEGACY_REPORT_HOMES: Partial<
    Record<(typeof NavigationSiteInsightsReports)[number], SiteNewAnalyticsReport>
> = {
    mcp: 'agents',
    ask: 'assistant',
    pages: 'traffic',
    search: 'traffic',
    links: 'traffic',
    openapi: 'traffic',
    urls: 'traffic',
};

/** Legacy reports whose data Engagement took over, once it is on. */
const ENGAGEMENT_REPORT_HOMES: Partial<
    Record<(typeof NavigationSiteInsightsReports)[number], SiteNewAnalyticsReport>
> = {
    search: 'engagement',
    links: 'engagement',
};

/** The rebuilt report holding a given report's data, so an old link lands on it rather than Traffic. */
export function getSiteNewAnalyticsReportHome(
    report: (typeof NavigationSiteInsightsReports)[number],
    context: Pick<SiteNewAnalyticsTabContext, 'isEngagementEnabled'>
): SiteNewAnalyticsReport {
    return (
        (context.isEngagementEnabled ? ENGAGEMENT_REPORT_HOMES[report] : undefined) ??
        LEGACY_REPORT_HOMES[report] ??
        'traffic'
    );
}

/**
 * Return the trialing plan for each rebuilt report a paid feature covers whole — which Traffic is
 * not, only its Pages & feedback section being paid.
 */
export function useSiteNewAnalyticsTrialingPlans(
    site: api.Site
): Partial<Record<SiteNewAnalyticsReport, string | null>> {
    const aiInsightsPlan = useTrialingSiteFeaturePlan(site, api.SiteFeatureKey.SitesAiInsights);

    return { assistant: aiInsightsPlan };
}
